import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

// Component
import Input from "@/components/UI/Input";
import DefaultButton from "@/components/buttons/DefaultButton/DefaultButton";
import PopupModal from "@/components/PopupModal/PopupModal";
import ToastNotify from "@/components/UI/ToastNotify";

// Hook
import { setToken } from "@/utils/token";
import { loginUser, loginWithGoogle, forgetPassword } from "@/api/auth";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";

const Login = () => {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = React.useState(i18n.language);

  // State
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isForgetPassowrd, setIsForgetPassword] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  // Hook
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  const navigate = useNavigate();

  const { mutate, isLoading: isLoadingPost } = useMutation(loginUser, {
    onSuccess: (data) => {
      setToken(data.data.access_token);
      navigate("/");
    },
    onError: (error: any) => {
      setError(error.response.data.detail);
    },
    retry: false,
  });

  const { mutate: submitForgetPassword, isLoading: isLoadingForgetPassword } = useMutation(
    forgetPassword,
    {
      onSuccess: (data) => {
        if (data.status === 200) {
          setTimeout(() => setIsForgetPassword(false), 3000);
          toast.success("Please check your email to reset your password.");
        }
      },
      onError: (error: any) => {
        setError(error.response.data.detail);
      },
      retry: false,
    }
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    mutate({
      email,
      password,
    });
  };

  useEffect(() => {
    if (code) loginWithGoogle(code);
  }, [code]);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setLanguage(event.target.value);
    i18n.changeLanguage(event.target.value);
    localStorage.setItem("language", event.target.value);
  };

  return (
    <div
      className="h-screen lg:flex lg:justify-center lg:items-center relative "
      data-testid="login-page">
      <div className="absolute top-1 right-1 z-[999]">
        <select
          onChange={handleChange}
          id="countries"
          defaultValue={language}
          className="bg-gray-50 border border-gray-300 active:border-blue-500 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2">
          <option value="en">{t("english")}</option>
          <option value="tr">{t("turkish")}</option>
        </select>
      </div>

      <PopupModal closeAction={() => setIsForgetPassword(false)} isPopup={isForgetPassowrd}>
        <div className="flex flex-col gap-10 ">
          <p>
            Enter the email address you used to join and we'll send you instructions to reset your
            password. <br />
            <br />
            For security reasons, we do NOT store your password. Therefore, you can rest assured
            that we will never send your password via email.
          </p>
          <Input
            placeholder="E-mailinizi giriniz"
            name="email"
            title="E-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required={true}
          />
          {error && <span className="text-dangerButtonPrimary text-sm -my-2">{error}</span>}
          <DefaultButton
            isDisabled={email.length === 0}
            onClick={() => submitForgetPassword(email)}
            isLoading={isLoadingForgetPassword}>
            {t("send")}
          </DefaultButton>
        </div>
      </PopupModal>
      <div className="flex flex-col lg:w-[460px] w-full relative">
        <form
          className="flex flex-col gap-5 col-span-1 p-[30px] bg-mainBackground lg:rounded-[50px] h-screen lg:h-fit"
          onSubmit={(e) => handleSubmit(e)}>
          <div className="mb-8">
            <div className="text-[42px] font-medium text-center">{t("welcome")}</div>
            <div className="text-center text-xl my-1.5">{t("to")}</div>
            <div className="w-full flex justify-center">
              <img src="/assets/logos/mikrolo-purple-dot.svg" alt="" width={218} />
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Input
              placeholder={t("enterYourEmail")}
              name="email"
              title={t("email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required={true}
            />
            <div className="flex flex-col gap-2 items-end">
              <Input
                placeholder={t("enterYourPassword")}
                name="password"
                title={t("password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                required={true}
              />
              <button
                className="text-sm text-sky"
                onClick={() => {
                  setIsForgetPassword(true);
                  setError(undefined);
                }}
                type="button">
                {t("forgotYourPassword")}
              </button>
            </div>
          </div>
          {!isForgetPassowrd && error && (
            <span className="text-dangerButtonPrimary text-sm -my-2">{error}</span>
          )}
          <div className="mt-5 w-full flex justify-center">
            <DefaultButton
              className="w-full"
              isDisabled={password.length === 0 || email.length === 0}
              isLoading={isLoadingPost}>
              {t("login")}
            </DefaultButton>
          </div>

          <span className="text-center text-sm text-textColor">
            {t("dontHaveAnAccount")}{" "}
            <Link to="/register" className="text-sky">
              {t("registerNow")}
            </Link>
            .
          </span>
        </form>
      </div>
      <ToastNotify />
    </div>
  );
};

export default Login;
